import React, { useState, useEffect, ReactElement } from 'react';
import type { Dispatch } from 'redux';
import { useDispatch } from 'react-redux'
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { MultiSelect } from 'primereact/multiselect';
import Moment from 'moment';
import { SearchBase } from '../../../../types/common';
import { DATETIME_FORMAT, MOBILE_WINDOW_BREAKPOINT, DEFAULT_PAGE_SIZE, supportTicketStatuses } from '../../../../constants/constants';
import { actions as supportActions } from '../../../../actions/supportActions';
import * as types from '../../../../constants/actionTypes';
import Notification from '../../../Common/Notification';
import { getSearchParamSortFields, getDataTableSortFields } from '../../../../helpers/sortHelper';
import { createdFromNow } from '../../../../helpers/dateTime';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import AgentSupportDrawer from '../../../Support/AgentTicketDrawer/AgentTicketDrawer';
import './ProjectSupportTickets.scss';

interface ProjectSupportTicketsProps {
    subscriptionId: number;
    projectId: number;
    windowWidth: number;
}

interface StatusOption {
    name: string;
    id: number
}

interface ProjectSupportTicketSearchParams extends SearchBase {
    subscriptionId: number,
    projectId: number,
    agentName: string,
    subject: string,
    requestorName: string,
    statuses: Array<number>
}

interface TicketItem {
    id: number,
    agentId: number,
    agentName: string,
    subject: string,
    status: number,
    createdByName: string,
    createdDate: Date | null
}

const ProjectSupportTickets = ({ subscriptionId, projectId, windowWidth }: ProjectSupportTicketsProps): ReactElement => {

    const statusOptions = [
        { name: "Open", id: supportTicketStatuses.Open },
        { name: "Pending", id: supportTicketStatuses.Pending },
        { name: "Solved", id: supportTicketStatuses.Solved }
    ];

    const supportTicket: TicketItem = {
        id: 0,
        agentId: 0,
        agentName: "",
        subject: "",
        status: 0,
        createdByName: "",
        createdDate: null
    };

    const [isInit, setIsInit] = useState<boolean>(true);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [tickets, setTickets] = useState<Array<TicketItem>>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [searchParams, setSearchParams] = useState<ProjectSupportTicketSearchParams>({
        subscriptionId: subscriptionId,
        projectId: projectId,
        agentName: "",
        subject: "",
        requestorName: "",
        statuses: [supportTicketStatuses.Open, supportTicketStatuses.Pending],
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy: "createdDate",
        sortDir: "desc"
    });
    const [selectedTicket, setSelectedTicket] = useState<TicketItem>(supportTicket);
    const [selectedStatuses, setSelectedStatuses] = useState<Array<StatusOption>>([statusOptions[0], statusOptions[1]]);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [agents, setAgents] = useState<Array<any>>([]);
    const [searchTrigger, setSearchTrigger] = useState<boolean>(false);

    const dispatch: Dispatch = useDispatch();
    const loadListFilters = async (subId: number) => dispatch(supportActions.loadListFilters(subId));
    const searchTickets = async (params) => dispatch(supportActions.searchTickets(params));
    const updateAgent = async (agentId: number, ticketId: number) => dispatch(supportActions.updateAgent(agentId, ticketId));

    useEffect(() => {

        const fetchData = async () => {

            try {
                await Promise.all([loadFilters(), search(searchParams)]);
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            }

            setIsInit(false);
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (!isInit)
            search(searchParams);
    }, [searchTrigger]);

    const loadFilters = async () => {

        try {
            setIsFetching(true);
            const result = await loadListFilters(subscriptionId);
            const payload = result.payload;
            if (result.type === types.LOAD_SUPPORT_TICKET_FILTERS_SUCCESS) {
                setAgents(payload.agents);
            } else if (result.type === types.LOAD_SUPPORT_TICKET_FILTERS_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        } finally {
            setIsFetching(false);
        }
    }

    const search = async (params: ProjectSupportTicketSearchParams) => {

        try {
            setIsFetching(true);
            const result = await searchTickets(params);
            const payload = result.payload;
            if (result.type === types.SEARCH_SUPPORT_TICKET_SUCCESS) {
                setTickets(payload.results);
                setTotalCount(payload.totalCount);
            } else if (result.type === types.SEARCH_SUPPORT_TICKET_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        } finally {
            setIsFetching(false);
        }
    }

    const filterChange = (e) => {
        setSearchParams((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }));
        setSearchTrigger((prevState) => !prevState);
    }

    const keyUpName = (e) => {
        if (e.keyCode == 13) {
            setSearchTrigger((prevState) => !prevState);
        }
    }

    const onSearchBlur = () => {
        setSearchTrigger((prevState) => !prevState);
    }

    const onPageChange = (e) => {
        setSearchParams((prevState) => ({
            ...prevState,
            pageNumber: e.page + 1
        }));
        setSearchTrigger((prevState) => !prevState);
    }

    const onChangeAgent = async (agentId) => {
        try {
            setIsFetching(true);
            const result = await updateAgent(agentId, selectedTicket.id);
            const payload = result.payload;
            if (result.type === types.UPDATE_SUPPORT_TICKET_AGENT_SUCCESS) {
                Notification("success", "Agent Reassigned Successfully", "")
                setSearchTrigger((prevState) => !prevState);
            } else if (result.type === types.UPDATE_SUPPORT_TICKET_AGENT_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        } finally {
            setIsFetching(false);
        }
    }

    const onStatusChange = (e) => {
        setSelectedStatuses(e.value);

        let statusIds: Array<number> = [];
        if (e.value.length > 0) {
            statusIds = e.value.map((item) => { return item.id });
        }

        setSearchParams((prevState) => ({
            ...prevState,
            statuses: statusIds
        }));
        setSearchTrigger((prevState) => !prevState);
    }

    const onDrawerComplete = () => {
        setIsDrawerOpen(false);
        setSearchTrigger((prevState) => !prevState);
    }

    const selectTicket = (e) => {

        // In certain cases the event is not a ticket selection but a random click that produces a selection event
        if (e.value === null)
            return;

        // if they're targeting the select box in the agent column then don't open the drawer
        const element = e.originalEvent.target.closest('.select-control');

        if (!element) {
            setSelectedTicket(e.value);
            setIsDrawerOpen(true);
        } else {
            setSelectedTicket(e.value);
        }
    }

    const agentTemplate = (rowData) => {
        const agentId = (rowData.agentId) ? rowData.agentId.toString() : "";
        return <MultiSelectBox name="agentId" label="Agent" placeholder="Agent" idField="id" valueField="name" options={agents} selectedValues={agentId} onChange={onChangeAgent} isMulti={false} className="select-control" />
    }

    const statusTemplate = (rowData) => {
        let retval = "";
        switch (rowData.status) {
            case supportTicketStatuses.Open:
                retval = "Open";
                break;
            case supportTicketStatuses.Pending:
                retval = "Pending";
                break;
            case supportTicketStatuses.Solved:
                retval = "Solved";
                break;
        }
        return retval;
    }

    const createdDateTemplate = (rowData) => {
        return Moment(rowData.createdDate).format(DATETIME_FORMAT);
    }

    const createdFromNowTemplate = (rowData) => {
        return createdFromNow(rowData.createdDate);
    }

    const onSort = async ({ sortField, sortOrder }) => {
        const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
        if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
            const newSearchParams = {
                ...searchParams,
                sortBy: sortBy,
                sortDir: sortDir
            }
            setSearchParams(newSearchParams);
            await search(newSearchParams);
        }
    }

    const displayColumns = (windowWidth < MOBILE_WINDOW_BREAKPOINT) ? 'none' : '';

    const agentFilterElement = <input name="agentName" type="text" className="p-inputtext" placeholder="Agent Name" size={50} onBlur={onSearchBlur} onChange={filterChange} onKeyUp={keyUpName} value={searchParams.agentName} />;

    const subjectFilterElement = <input name="subject" type="text" className="p-inputtext" placeholder="Subject" size={50} onBlur={onSearchBlur} onChange={filterChange} onKeyUp={keyUpName} value={searchParams.subject} />;

    const requestorFilterElement = <input name="requestorName" type="text" className="p-inputtext" placeholder="Requestor" size={50} onBlur={onSearchBlur} onChange={filterChange} onKeyUp={keyUpName} value={searchParams.requestorName} />;

    const statusFilter =
        <MultiSelect className="ui-column-filter" optionLabel="name"
            appendTo={document.body}
            value={selectedStatuses}
            options={statusOptions}
            onChange={onStatusChange}
            maxSelectedLabels={1} />;

    const { sortField, sortOrder } = getDataTableSortFields(searchParams);

    return (
        <div>
            {isDrawerOpen &&
                <AgentSupportDrawer ticketId={selectedTicket.id.toString()} onComplete={onDrawerComplete} />
            }
            <div className="project-support-tickets-section">

                <LoadingIndicator isLoading={isFetching} hideWhileLoading={true}>
                    <div className="identifier-results-table">
                        <DataTable
                            filterDisplay="row"
                            className="p-datatable-sm p-datatable-striped"
                            value={tickets}
                            selectionMode="single"
                            selection={selectedTicket}
                            onSelectionChange={selectTicket}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={onSort} >
                            <Column field="agentName" header="Agent" filter={true} filterElement={agentFilterElement} body={agentTemplate} sortable={true} />
                            <Column field="subject" header="Subject" filter={true} filterElement={subjectFilterElement} style={{ display: displayColumns }} sortable={true} />
                            <Column field="createdByName" header="Requestor" filter={true} filterElement={requestorFilterElement} style={{ display: displayColumns }} sortable={true} />
                            <Column field="createdDate" header="Created" body={createdDateTemplate} sortable={true} />
                            <Column field="createdFromNow" header="Time" body={createdFromNowTemplate} style={{ display: displayColumns }} sortable={true} />
                            <Column field="status" header="Status" body={statusTemplate} filter={true} filterElement={statusFilter} sortable={true} />
                        </DataTable>
                        {
                            totalCount > searchParams.pageSize &&
                            <Paginator
                                rows={searchParams.pageSize}
                                totalRecords={totalCount}
                                first={(searchParams.pageNumber - 1) * searchParams.pageSize}
                                onPageChange={onPageChange} />
                        }
                        <div className="results-count">Total records: {totalCount}</div>
                    </div>
                </LoadingIndicator>

            </div>
        </div>
    );
};

export default ProjectSupportTickets;
